/** Live floor-plan area (`model.area`). */

import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

import type { UppKonvaDrawMode } from './upp-konva-types';
import type { UppKonvaLabelFontSize } from './upp-konva-types';
import type { UppKonvaOrientation } from './upp-konva-types';
import type { UppKonvaAreaSource } from './upp-konva-source';
import type { UppKonvaDestroy } from './upp-konva-destroy';

import { uppKonvaShapeFromSource } from './shapes';
import { uppKonvaShapePatchFromSource } from './shapes';
import { uppKonvaShapeToSource } from './shapes';

import { UppKonvaGroup } from './upp-konva-group';
import { UppKonvaShape } from './upp-konva-shape';
import { UppKonvaStage } from '../stage/upp-konva-stage';
import { uppKonvaAreaWire } from '../stage/upp-konva-stage';

import { parseUppKonvaLabelFontSize } from '../stage/plan/floor-plan-preferences';
import { shapeSourceToWireRow } from '../stage/plan/adapters/shape-bridge/shape-bridge-registry';
import { resolveFloorPlanDrawRowUuid } from '../stage/internal/floor-plan-uuid';
import { uppKonvaRuntimeTrace } from '../internal/upp-konva-runtime-trace';

const _DEFAULT_ORIENTATION: UppKonvaOrientation = 'AUTO';
const _DEFAULT_DRAW_MODE: UppKonvaDrawMode = 'SHAPES';
const _DEFAULT_LABEL_SIZE: UppKonvaLabelFontSize = 'NORMAL';

function _shapesFingerprint(shapes: readonly UppKonvaShape[]): string {
    return JSON.stringify(shapes.map((s) => ({ id: s.id, kind: s.kind })));
}

/** Fingerprint of persistable rows (including `data`), not only id/kind. */
function _shapesSourceRowsFingerprint(shapes: readonly UppKonvaShape[]): string {
    return JSON.stringify(shapes.map((s) => uppKonvaShapeToSource(s)));
}


/**
 * After the public area is pushed to the scene, the wire payload may carry `TABLE.shape` not yet aligned with
 * `UppKonvaShape.data`. Persistable **`data.shape`** is owned by live shapes; the wire supplies geometry and the rest of `data`.
 *
 * @param liveArea - Live shapes (TABLE rows used for merge).
 * @param incoming - Incoming wire-aligned area source.
 * @returns Merged area source with TABLE `data.shape` reconciled from live shapes.
 */
function _mergeIncomingTableShapeDataFromLivePublic(
    liveArea: { readonly shapes: readonly UppKonvaShape[] },
    incoming: UppKonvaAreaSource,
): UppKonvaAreaSource {
    const liveById = new Map<string, UppKonvaShape>();
    for (const s of liveArea.shapes) {
        if (s.kind === 'TABLE') {
            liveById.set(s.id.trim(), s);
        }
    }
    if (liveById.size === 0) {
        return incoming;
    }
    const shapes = incoming.shapes.map((row) => {
        if (row.kind !== 'TABLE') {
            return row;
        }
        const rowId = row.id?.trim() ?? '';
        if (rowId === '') {
            return row;
        }
        const live = liveById.get(rowId);
        if (!live) {
            return row;
        }
        const liveShape = live.data['shape'];
        if (liveShape === undefined) {
            return row;
        }
        const fromWire =
            typeof row.data === 'object' && row.data !== null && !Array.isArray(row.data)
                ? { ...(row.data as Record<string, unknown>) }
                : {};
        return {
            ...row,
            data: {
                ...fromWire,
                shape: liveShape,
            },
        };
    });
    return { ...incoming, shapes };
}

/**
 * Live floor-plan area (`model.area`). Inside an `UppKonvaModel` the reference is never replaced.
 *
 * To hydrate from the app, assign `area.source = …`. That and assigning `shapes` emit `onSourceChanged$` on structural shape
 * changes, DTO `data` changes (same id/kind list), or room metadata changes.
 *
 * `doDestroy` completes observables and destroys every shape in `shapes`.
 *
 * @remarks
 * **Integrator-facing surface** (guide + public TypeDoc “model”): room dimensions and metadata, `source` / `shapes`,
 * `onSourceChanged$`, `onShapeAdded$`, `onGrouped$`, `notifyPresentationDirty`, `removeShapes` / `removeShape`,
 * `selectShape`, `onClearSelection$`, `shapesStructuralFingerprint`, `findShapeById`, `doDestroy`. Per-shape canvas attention: {@link UppKonvaShape.attention}.
 *
 * **Package-internal hooks** (JSDoc internal tag: omitted from TypeDoc with `excludeInternal`, stripped from published `.d.ts`
 * with `stripInternal`, and **not** part of the integrator narrative): stage wiring (`wireKonvaStage`,
 * `applyAdapterSnapshotFromStage`, stage replay suppression, `notifyGrouped`,
 * `requestRepaint`). Konva host references: {@link import('../stage/upp-konva-stage').UppKonvaStage},
 * {@link uppKonvaAreaWire}, tracing {@link import('../internal/upp-konva-runtime-trace').uppKonvaRuntimeTrace}.
 */
export class UppKonvaArea implements UppKonvaDestroy {
    // --------------------------------------------------------------------------
    // 1. Lifecycle
    // --------------------------------------------------------------------------

    /**
     * Creates an area from a DTO (for example hydrated JSON).
     *
     * @param source - Initial `UppKonvaAreaSource`.
     * @returns A new `UppKonvaArea` instance.
     */
    static from(source: UppKonvaAreaSource): UppKonvaArea {
        return new UppKonvaArea(source);
    }

    /**
     * Initializes dimensions, metadata, and shapes from `source`.
     *
     * @param source - Initial DTO.
     */
    constructor(source: UppKonvaAreaSource) {
        this._width = 0;
        this._height = 0;
        this._orientation = _DEFAULT_ORIENTATION;
        this._mode = _DEFAULT_DRAW_MODE;
        this._labelSize = _DEFAULT_LABEL_SIZE;
        this._shapes = [];

        void this._applyFromSource(source, false);
    }

    /**
     * Calls `doDestroy` on each shape, completes `onGrouped$`, `onShapeAdded$`, and `onSourceChanged$`, and clears `shapes`. Idempotent.
     */
    doDestroy(): void {
        if (this._destroyed) {
            return;
        }
        this._destroyed = true;
        this._konvaStage = null;
        this._repaintPending = false;
        for (const s of this._shapes) {
            s.bindParentArea(null);
            s.bindKonvaStage(null);
            s.doDestroy();
        }
        this._shapes = [];
        this._onGrouped$.complete();
        this._onShapeAdded$.complete();
        this._onSourceChanged$.complete();
        this._onClearSelection$.complete();
    }

    // --------------------------------------------------------------------------
    // 2. Properties & observables (integrator)
    // --------------------------------------------------------------------------
    // Private RxJS subjects must be declared before the `readonly …$` fields that reference them.

    private readonly _onGrouped$ = new Subject<UppKonvaGroup | null>();
    private readonly _onSourceChanged$ = new Subject<void>();
    private readonly _onShapeAdded$ = new Subject<UppKonvaShape>();
    private readonly _onClearSelection$ = new Subject<void>();

    /**
     * Emits once when the **host** selection goes from **at least one** element to **none** (same report the stage sends as
     * empty UUID list to **`upp-konva`**). Not emitted on initial empty state; idempotent across repeated empty reports.
     */
    readonly onClearSelection$: Observable<void> = this._onClearSelection$.asObservable();

    /**
     * Multi-selection observable on the canvas; not part of the plan JSON.
     * After `UppKonvaModel` wiring, a homogeneous multi-selection emits an `UppKonvaGroup` with a stage delegate;
     * `null` when zero or one item is selected.
     */
    readonly onGrouped$: Observable<UppKonvaGroup | null> = this._onGrouped$.asObservable();

    /**
     * Emits after `source = …` when the structural `shapes` fingerprint changes, any row’s serializable payload
     * (`data` / DTO geometry), or room metadata (orientation, draw mode, dimensions, etc.).
     * Does not emit on the constructor’s initial load. `UppKonvaModel` repaints the scene on every emission; `onModelChanged`
     * should only track structural list changes.
     */
    readonly onSourceChanged$: Observable<void> = this._onSourceChanged$.asObservable();

    /**
     * Emits once per **new** document row (`id` not present before the update): after `source = …`, `shapes = …`, or
     * `applyAdapterSnapshotFromStage` (via the host). Does **not** emit for the initial `UppKonvaArea.from` / `new UppKonvaArea`
     * load, nor when rows are only updated or reordered (same ids). Replacing a row under an existing id is not an “add”.
     */
    readonly onShapeAdded$: Observable<UppKonvaShape> = this._onShapeAdded$.asObservable();

    /** Plan width in cm. */
    get width(): number {
        return this._width;
    }

    /** Plan height in cm. */
    get height(): number {
        return this._height;
    }

    /** Optional plan name. */
    get name(): string | undefined {
        return this._name;
    }

    /** Canvas fit (not shape rotation). */
    get orientation(): UppKonvaOrientation {
        return this._orientation;
    }

    /** Area draw mode (`SHAPES` / `VERTEX`); distinct from `UppKonvaModel.mode` VIEW/EDIT. */
    get mode(): UppKonvaDrawMode {
        return this._mode;
    }

    /** Label / sublabel badge text scale on the canvas. */
    get labelSize(): UppKonvaLabelFontSize {
        return this._labelSize;
    }

    /** Live model shapes (one instance per document row). */
    get shapes(): readonly UppKonvaShape[] {
        return this._shapes;
    }

    /**
     * Replaces the shape list. Emits `onSourceChanged$` when the structural fingerprint (id + kind order) changes.
     */
    set shapes(value: ReadonlyArray<UppKonvaShape>) {
        if (this._destroyed) {
            return;
        }

        const fpBefore = _shapesFingerprint(this._shapes);
        const idsBefore = new Set(this._shapes.map((s) => s.id.trim()));
        this._shapes = [...value];
        this._rebindShapeContext();

        if (!this._destroyed) {
            for (const s of this._shapes) {
                if (!idsBefore.has(s.id.trim())) {
                    this._onShapeAdded$.next(s);
                }
            }
        }

        if (fpBefore !== _shapesFingerprint(this._shapes)) {
            this._onSourceChanged$.next();
        }
    }

    /**
     * Ordered `id` + `kind` fingerprint; same notion of structural change as `shapes` / `onSourceChanged$`.
     *
     * @returns Structural fingerprint string.
     */
    get shapesStructuralFingerprint(): string {
        return _shapesFingerprint(this._shapes);
    }

    /** DTO view of the current state (serializable; round-trips with `source = …`). */
    get source(): UppKonvaAreaSource {
        const base: UppKonvaAreaSource = {
            width: this._width,
            height: this._height,
            name: this._name,
            orientation: this._orientation,
            mode: this._mode,
            shapes: this._shapes.map((s) => uppKonvaShapeToSource(s)),
            ...(this._labelSize !== _DEFAULT_LABEL_SIZE ? { labelSize: this._labelSize } : {}),
        };
        return this._schemaVersion !== undefined ? { ...base, schemaVersion: this._schemaVersion } : base;
    }

    /**
     * Applies an `UppKonvaAreaSource` in place. Emits `onSourceChanged$` when the structural `shapes` fingerprint changes,
     * any row’s DTO payload (for example `data.shape` without id/kind changes), or room metadata.
     *
     * @param value - Full area DTO.
     */
    set source(value: UppKonvaAreaSource) {
        if (this._destroyed) {
            return;
        }
        const rowsBefore = _shapesSourceRowsFingerprint(this._shapes);
        const before = {
            w: this._width,
            h: this._height,
            orientation: this._orientation,
            mode: this._mode,
            labelSize: this._labelSize,
            name: this._name,
            schemaVersion: this._schemaVersion,
        };
        const shapesStructural = this._applyFromSource(value, true);
        const rowsChanged = rowsBefore !== _shapesSourceRowsFingerprint(this._shapes);
        const roomOrMetaChanged =
            before.w !== this._width ||
            before.h !== this._height ||
            before.orientation !== this._orientation ||
            before.mode !== this._mode ||
            before.labelSize !== this._labelSize ||
            before.name !== this._name ||
            before.schemaVersion !== this._schemaVersion;
        if (shapesStructural || rowsChanged || roomOrMetaChanged) {
            this._onSourceChanged$.next();
        }
    }

    // --------------------------------------------------------------------------
    // 3. Public methods (integrator)
    // --------------------------------------------------------------------------

    /**
     * After mutating `data` or other live fields without assigning `source`, emits `onSourceChanged$` so the model
     * repaints the scene.
     */
    notifyPresentationDirty(): void {
        if (this._destroyed) {
            return;
        }
        this._onSourceChanged$.next();
    }

    /**
     * Host selection sync: invoked by **`upp-konva`** with the number of ids the floor-plan stage reported for the current
     * selection (`0` when cleared). Emits {@link onClearSelection$} on the transition from non-zero to zero.
     *
     * @param selectedElementCount - `elementUuids.length` from the stage’s selection callback.
     */
    notifyHostSelectionFromStage(selectedElementCount: number): void {
        if (this._destroyed) {
            return;
        }
        const nowNonEmpty = selectedElementCount > 0;
        if (this._lastStageSelectionNonEmpty && !nowNonEmpty) {
            this._onClearSelection$.next();
        }
        this._lastStageSelectionNonEmpty = nowNonEmpty;
    }

    /**
     * Resolves a live shape by public model `id` or by the canonical floor-plan element uuid (same rule as the host’s
     * selection ids when `shape.id` is not already a v4 string).
     *
     * @param id - `UppKonvaShape.id` (trimmed) or canonical element uuid from the canvas.
     * @returns Matching `UppKonvaShape`, or `null`.
     */
    findShapeById(id: string): UppKonvaShape | null {
        return this._findShapeById(id);
    }

    /**
     * Removes the given shapes from `shapes`, calls `doDestroy` on each, and emits `onSourceChanged$` when the structural
     * fingerprint changes. When a stage is wired, clears the Konva host selection.
     *
     * @param shapes - Live shapes to remove (typically references from `this.shapes`).
     * @returns `true` when at least one shape was removed.
     */
    removeShapes(shapes: readonly UppKonvaShape[]): boolean {
        if (this._destroyed || shapes.length === 0) {
            return false;
        }
        const wanted = new Set(shapes);
        const inArea = this._shapes.filter((s) => wanted.has(s));
        if (inArea.length === 0) {
            return false;
        }
        const fpBefore = _shapesFingerprint(this._shapes);
        const removeSet = new Set(inArea);
        this._shapes = this._shapes.filter((s) => !removeSet.has(s));
        for (const s of inArea) {
            s.notifyRemoved();
            s.bindParentArea(null);
            s.bindKonvaStage(null);
            s.doDestroy();
        }
        if (fpBefore !== _shapesFingerprint(this._shapes)) {
            this._onSourceChanged$.next();
        }
        this._konvaStage?.clearHostSelection();
        return true;
    }

    /**
     * Same as `removeShapes` with a single shape.
     *
     * @param shape - Shape to remove.
     * @returns `true` when the shape was removed.
     */
    removeShape(shape: UppKonvaShape): boolean {
        return this.removeShapes([shape]);
    }

    /**
     * Selects a live public shape on the mounted canvas when a Konva stage is wired (`UppKonvaModel` does this for the host).
     *
     * @param shape - Shape from `this.shapes`; pass `null` to clear host selection.
     * @returns `true` when the operation was accepted (`null` clears best-effort when not destroyed). Returns `false` when
     *     destroyed, the id is empty, the shape is not in `this.shapes`, or a non-null selection was requested without a wired stage.
     */
    selectShape(shape: UppKonvaShape | null): boolean {
        if (this._destroyed) {
            return false;
        }
        if (shape === null) {
            this._konvaStage?.clearHostSelection();
            return true;
        }
        const id = shape.id.trim();
        if (id === '') {
            return false;
        }
        const live = this._shapes.some((candidate) => candidate.id.trim() === id);
        if (!live) {
            return false;
        }
        const stage = this._konvaStage;
        if (!stage) {
            return false;
        }
        const hostUuid = this._floorPlanHostSelectionUuid(shape);
        stage.selectHostOnly([hostUuid]);
        return true;
    }

    // --------------------------------------------------------------------------
    // 4. Public methods — package internal (@internal)
    // --------------------------------------------------------------------------

    /**
     * Notifies a multi-selection change on the canvas. Package-internal.
     *
     * @param value - Active group, or `null` when leaving multi-selection.
     * @internal
     */
    notifyGrouped(value: UppKonvaGroup | null): void {
        if (this._destroyed) {
            return;
        }
        this._onGrouped$.next(value);
    }

    /**
     * Wires the Konva engine to this area. Only `UppKonvaModel` should call this.
     *
     * @param stage - Konva stage bridge.
     * @internal
     */
    wireKonvaStage(stage: UppKonvaStage): void {
        this._konvaStage = stage;
        this._rebindShapeContext();
    }

    /**
     * Whether replaying the public snapshot into the stage is suppressed (import / wire paths).
     *
     * @returns `true` while suppression depth is positive.
     * @internal
     */
    sceneReplayToStageSuppressed(): boolean {
        return this._sceneReplaySuppressionDepth > 0;
    }

    /**
     * Runs `fn` without replaying the public snapshot into the stage.
     *
     * @param fn - Callback to run under suppression.
     * @internal
     */
    withSceneReplayToStageSuppressed(fn: () => void): void {
        this._sceneReplaySuppressionDepth += 1;
        try {
            fn();
        } finally {
            this._sceneReplaySuppressionDepth -= 1;
        }
    }

    /**
     * Applies the stage adapter snapshot onto the public area. Invoked by the `upp-konva` host on structural engine changes.
     *
     * @param snapshot - Adapter snapshot (may include `onGrouped` metadata).
     * @internal
     */
    applyAdapterSnapshotFromStage(snapshot: UppKonvaAreaSource & { onGrouped?: unknown }): void {
        if (this._destroyed) {
            return;
        }

        uppKonvaRuntimeTrace('UppKonvaArea.applyAdapterSnapshotFromStage (stage → public area)', {
            tableShapes: snapshot.shapes
                .filter((r) => r.kind === 'TABLE')
                .map((r) => ({
                    shape:
                        typeof r.data === 'object' && r.data !== null && !Array.isArray(r.data)
                            ? (r.data as Record<string, unknown>)['shape']
                            : undefined,
                })),
        });

        this.withSceneReplayToStageSuppressed(() => {
            let source = uppKonvaAreaWire(snapshot);
            source = _mergeIncomingTableShapeDataFromLivePublic(this, source);
            this.source = source;
        });
    }

    /**
     * Requests a stage `doRefresh`. Multiple calls in the same microtask coalesce to a single repaint.
     * Called from live shapes in this package; not part of the integrator contract.
     *
     * @internal
     */
    requestRepaint(): void {
        if (this._destroyed || !this._konvaStage) {
            return;
        }

        if (this._repaintPending) {
            return;
        }

        this._repaintPending = true;

        queueMicrotask(() => {
            this._repaintPending = false;
            if (!this._destroyed && this._konvaStage) {
                this._konvaStage.doRefresh();
            }
        });
    }

    /** Resolves a live shape for a canonical scene id. */
    private _findShapeById(id: string): UppKonvaShape | null {
        if (this._destroyed) {
            return null;
        }

        const t = id.trim();
        for (const s of this._shapes) {
            if (s.id.trim() === t) {
                return s;
            }
        }

        for (const s of this._shapes) {
            if (UppKonvaStage.floorPlanElementUuid(s.id.trim()) === t) {
                return s;
            }
        }

        return null;
    }

    /**
     * Canonical scene row uuid for host selection (same rule as `floorPlanModelFromAreaSource` in the stage adapters).
     */
    private _floorPlanHostSelectionUuid(shape: UppKonvaShape): string {
        const wireRow = shapeSourceToWireRow(uppKonvaShapeToSource(shape));
        return resolveFloorPlanDrawRowUuid(wireRow as unknown as Record<string, unknown>);
    }

    // --------------------------------------------------------------------------
    // 5. Private members
    // --------------------------------------------------------------------------

    private _destroyed = false;
    private _width: number;
    private _height: number;
    private _name?: string;
    private _orientation: UppKonvaOrientation;
    private _mode: UppKonvaDrawMode;
    private _labelSize: UppKonvaLabelFontSize;
    private _schemaVersion?: number;
    private _shapes: UppKonvaShape[];

    /** Wired by {@link import('./upp-konva-model').UppKonvaModel} after the Konva engine is constructed. */
    private _konvaStage: UppKonvaStage | null = null;

    /**
     * Suppresses replaying the public snapshot into the stage while applying a wire/import snapshot already coherent with the canvas.
     *
     * @see {@link UppKonvaModel} subscription to {@link onSourceChanged$}.
     */
    private _sceneReplaySuppressionDepth = 0;

    /** True while a coalesced `requestRepaint` microtask is scheduled. */
    private _repaintPending = false;

    /** Last non-empty host selection report from {@link notifyHostSelectionFromStage}; drives {@link onClearSelection$}. */
    private _lastStageSelectionNonEmpty = false;

    /**
     * Replaces dimensions and shapes from a DTO in place (constructor and `source` setter).
     *
     * @param source - Incoming DTO.
     * @param emitShapeAdded - When `true`, emits {@link UppKonvaArea.onShapeAdded$} for each new `id` vs the list before apply.
     * @returns `true` when the `shapes` list changed structurally (id/kind fingerprint).
     */
    private _applyFromSource(source: UppKonvaAreaSource, emitShapeAdded: boolean): boolean {
        const fpBefore = _shapesFingerprint(this._shapes);
        const idsBefore = new Set(this._shapes.map((s) => s.id.trim()));
        const liveById = new Map<string, UppKonvaShape>();
        for (const shape of this._shapes) {
            const id = shape.id.trim();
            if (id !== '' && !liveById.has(id)) {
                liveById.set(id, shape);
            }
        }
        const reused = new Set<UppKonvaShape>();
        const consumedLiveIds = new Set<string>();
        const nextShapes: UppKonvaShape[] = [];
        this._width = source.width;
        this._height = source.height;
        this._name = source.name;
        this._orientation = (source.orientation ?? _DEFAULT_ORIENTATION) as UppKonvaOrientation;
        this._mode = (source.mode ?? _DEFAULT_DRAW_MODE) as UppKonvaDrawMode;
        this._labelSize = parseUppKonvaLabelFontSize(source.labelSize);
        this._schemaVersion = source.schemaVersion;
        for (const row of source.shapes) {
            const rowId = typeof row.id === 'string' ? row.id.trim() : '';
            const canReuse = rowId !== '' && !consumedLiveIds.has(rowId);
            const live = canReuse ? liveById.get(rowId) ?? null : null;
            if (live && uppKonvaShapePatchFromSource(live, row)) {
                reused.add(live);
                consumedLiveIds.add(rowId);
                nextShapes.push(live);
                continue;
            }
            const created = uppKonvaShapeFromSource(row);
            if (created) {
                nextShapes.push(created);
            }
        }
        for (const shape of this._shapes) {
            if (!reused.has(shape)) {
                shape.bindParentArea(null);
                shape.bindKonvaStage(null);
                shape.doDestroy();
            }
        }
        this._shapes = nextShapes;
        this._rebindShapeContext();
        if (emitShapeAdded && !this._destroyed) {
            for (const s of this._shapes) {
                if (!idsBefore.has(s.id.trim())) {
                    this._onShapeAdded$.next(s);
                }
            }
        }
        return fpBefore !== _shapesFingerprint(this._shapes);
    }

    /** {@link UppKonvaShape}: bind owning area + stage (when wired). */
    private _rebindShapeContext(): void {
        for (const s of this._shapes) {
            s.bindParentArea(this);
            s.bindKonvaStage(this._konvaStage);
        }
    }
}
